import React, { useEffect, useMemo, useState } from "react";
import { compose } from "recompose";
import { Table, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import classNames from "classnames";
import {
  TableContainer,
  Pagination,
  TableDataFetch,
  withTable,
  ViewingRecords,
  TableSearch,
} from "components/common/Tables";
import { Link } from "react-router-dom";
import { Page } from "components/common";
import { formatCurrency, formatDate } from "services/util";
import DatePicker from 'react-datepicker';

import { ApiClient } from "services";
import { Control, actions } from "react-redux-form";
import Select from "react-select";
import { FormGroup, withLocalForm } from "components/common";

export const InvoiceList = ({
  match,
  url = "subscription-invoices",
  ordering,
  setOrdering,
  data,
  setData,
  limit,
  offset,
  setOffset,
  search,
  setSearch,
  refreshToken,
  t,
  filters,
  setFilters,
  LocalForm,
  dispatch,
}) => {
  const [searchForm, setSearchForm] = useState({})
  const [status, setStatus] = useState("");
  const [organisationId, setOrganisationId] = useState("");
  const [organisations, setOrganisations] = useState([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorItem, setErrorItem] = useState(null);

  useEffect(() => {
    const client = ApiClient();
    client
      .get("connected_organisations", { params: { active: true } })
      .then((res) => setOrganisations(res.data || []))
      .catch(() => {});
  }, []);

  const handleSearchInputChange = (event) => {
    const v = event.target.value;
    setSearchForm({ ...searchForm, search: v });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchClick();
    }
  };

  const handleStartDateChange = (date) => {
    setSearchForm({ ...searchForm, from_date: date });
  };

  const handleEndDateChange = (date) => {
    setSearchForm({ ...searchForm, to_date: date });
  };

  const handleOrganisationChange = (v) => {
    setSearchForm({ ...searchForm, connected_organisation: v || undefined });
  };

  const handleStatusChange = (v) => {
    setSearchForm({ ...searchForm, status: v || undefined });
  };

  const handleSearchClick = () => {
    setFilters({
      ...filters,
      search: searchForm.search || undefined,
      issue_date__gte: searchForm.from_date ? formatDate(searchForm.from_date) : undefined,
      issue_date__lte: searchForm.to_date ? formatDate(searchForm.to_date) : undefined,
      connected_organisation: searchForm.connected_organisation || undefined,
      status: searchForm.status || undefined,
    });
  };

  const handleClearFilters = () => {
    setSearchForm({});
    dispatch(actions.reset("local"));
    setFilters({});
  };

  const openErrorModal = (item) => {
    setErrorItem(item);
    setErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setErrorModalOpen(false);
    setErrorItem(null);
  };

  const truncate = (s, n = 80) => {
    if (!s) return "";
    const str = String(s);
    return str.length > n ? str.slice(0, n) + "…" : str;
  };

  return (
    <Page title="Invoices">
      <LocalForm>
        <Row>
          <Col xs="12" md="3" className="my-1">
            <FormGroup label={t("Search")}>
              <Control.text
                model=".search"
                className="form-control mb-2"
                placeholder={t("Search by invoice number or organisation name")}
                type="text"
                value={searchForm.search || ""}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
              />
            </FormGroup>
          </Col>

          <Col xs="12" md="2" className="my-1">
            <FormGroup label={t("From Date")}>
              <DatePicker
                className="form-control"
                selected={searchForm.from_date}
                placeholderText={t("Select a Date")}
                onChange={handleStartDateChange}
                dateFormat="YYYY-MM-DD"
              />
            </FormGroup>
          </Col>

          <Col xs="12" md="2" className="my-1">
            <FormGroup label={t("To Date")}>
              <DatePicker
                className="form-control"
                selected={searchForm.to_date}
                placeholderText={t("Select a Date")}
                onChange={handleEndDateChange}
                dateFormat="YYYY-MM-DD"
              />
            </FormGroup>
          </Col>

          <Col xs="12" md="3" className="my-1">
            <FormGroup label={t("Organisation")} addControlClass={false}>
              <Control.text
                component={Select}
                style={{ minWidth: '8rem', backgroundColor: '#F4F7FE' }}
                model=".connected_organisation"
                simpleValue
                clearable={true}
                onChange={handleOrganisationChange}
                options={organisations.map((o) => ({ label: o.name, value: o.id }))}
              />
            </FormGroup>
          </Col>

          <Col xs="12" md="2" className="my-1">
            <FormGroup label={t("Status")} addControlClass={false}>
              <Control.text
                component={Select}
                style={{ minWidth: '8rem', backgroundColor: '#F4F7FE' }}
                model=".status"
                simpleValue
                clearable={false}
                onChange={handleStatusChange}
                options={[
                  { label: t('Unpaid'), value: 'Unpaid' },
                  { label: t('Paid'), value: 'Paid' },
                  { label: t('Cancelled'), value: 'Cancelled' },
                ]}
              />
            </FormGroup>
          </Col>

          <Col xs="12" md="auto" className="my-1 d-flex align-items-end ml-md-auto">
            <Button
              color="primary"
              type="button"
              onClick={handleSearchClick}
              className="mb-2 mr-2"
              title={t("Apply Filters")}
            >
              <span className="fa fa-filter mr-1" />
              {t("Apply Filters")}
            </Button>
            <Button
              color="secondary"
              type="button"
              onClick={handleClearFilters}
              className="mb-2 mr-0"
              title={t("Clear Filters")}
            >
              <span className="fa fa-times mr-1" />
              {t("Clear Filters")}
            </Button>
          </Col>
        </Row>
      </LocalForm>
      <TableContainer
        items={data.results}
        ordering={ordering}
        onOrderingChanged={setOrdering}
      >
        <Table size="sm">
          <thead>
            <tr>
              <th sortby="invoice_number">Invoice #</th>
              <th>Organisation</th>
              <th sortby="issue_date">Issue Date</th>
              <th sortby="due_date">Due Date</th>
              <th className="text-right">Sub Total</th>
              <th className="text-right">Total Tax</th>
              <th className="text-right">Total</th>
              <th>Sent</th>
              <th>Reminded</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((i) => (
              <tr
                key={i.id}
                className={classNames({
                  "table-warning": i.overdue,
                  "table-success": i.is_paid,
                })}
              >
                <td>{i.invoice_number}</td>
                <td>
                  <Link to={`/core/organisation/${i.connected_organisation_data?.id}`}>{i.connected_organisation_data?.name}</Link>
                </td>
                <td>{i.issue_date}</td>
                <td>{i.due_date}</td>
                <td className="text-right">
                  {formatCurrency(i.sub_total)} {i.currency}
                </td>
                <td className="text-right">
                  {formatCurrency(i.vat_amount)} {i.currency}
                </td>
                <td className="text-right">
                  {formatCurrency(i.total_amount)} {i.currency}
                </td>
                <td className="text-center">
                  <span
                    className={
                      i.invoice_sent
                        ? "fa fa-check-circle text-success"
                        : "fa fa-times-circle text-danger"
                    }
                  />
                </td>
                <td className="text-center">
                  <span
                    className={
                      i.reminder_sent
                        ? "fa fa-check-circle text-success"
                        : "fa fa-times-circle text-danger"
                    }
                  />
                </td>
                <td>{i.status}</td>
                <td>
                  {truncate(i.error_message, 80)}
                  {i.error_message && (
                    <Button size="sm" className="ml-2" onClick={() => openErrorModal(i)}>
                      {t("View")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <TableDataFetch
        ordering={ordering}
        refreshToken={refreshToken}
        search={search}
        limit={limit}
        url={url}
        offset={offset}
        onDataFetched={setData}
        filters={filters}
      />
      <ViewingRecords
        offset={offset}
        limit={limit}
        count={data.count}
        className="float-left"
      />
      <div className="float-right">
        <Pagination
          size="sm"
          count={data.count}
          limit={limit}
          offset={offset}
          onOffsetChanged={setOffset}
        />
      </div>
      <Modal isOpen={errorModalOpen} toggle={closeErrorModal} size="lg">
        <ModalHeader toggle={closeErrorModal}>{t("Error Details")}</ModalHeader>
        <ModalBody>
          {errorItem && (
            <div>
              <div className="mb-2"><strong>{t("Message")}</strong></div>
              <div className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>{errorItem.error_message || '-'}</div>
              <div className="mb-2"><strong>{t("Type")}</strong></div>
              <div className="mb-3">{errorItem.error_type || '-'}</div>
              <div className="mb-2"><strong>{t("Details")}</strong></div>
              <pre style={{ maxHeight: 300, overflow: 'auto' }}>{
                errorItem.error_details ? JSON.stringify(errorItem.error_details, null, 2) : '-'
              }</pre>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeErrorModal}>{t("Close")}</Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
}

const enhance = compose(
  withTable({ limit: 100 }),
  withLocalForm({
    initialState: {
      status: "",
      connected_organisation: "",
    },
  })
);

export default enhance(InvoiceList);
